import { useCreditStore } from '@stores/creditStore';
import { trialDaysLeft } from '@lib/services/credit/platformTrial';
import type { CreditOverview } from '../types/credit';

export interface PlatformTrialState {
  /** True only for an unapproved onboarding-3.0 platform trial — drives plan
   *  labels, the billing hero and the banner's copy. Does NOT by itself open
   *  the day-15 approval dialog; that triggers off `readOnly` alone, below. */
  awaitingApproval: boolean;
  /** The ONLY field write-locks may branch on — and what triggers the day-15
   *  approval dialog (`PlatformTrialController`) and the permanent
   *  `ReadOnlyBar`. */
  readOnly: boolean;
  daysLeft: number | null;
  creditsRemaining: number | null;
  creditsTotal: number | null;
  trialEndsAt: string | null;
}

/**
 * Non-hook selector for `overview.readOnly` — the single criterion every
 * write-lock branches on (see the "one criterion, one place" constraint).
 * Exported for the rare call site that only has the raw store state to hand
 * rather than a React render (e.g. `useWsChat`'s send guard, which reads
 * `useCreditStore.getState()` directly instead of subscribing); `usePlatformTrial`
 * itself is built on this same selector, so there is still exactly one rule.
 */
export function selectReadOnly(overview: CreditOverview | null | undefined): boolean {
  return overview?.readOnly === true;
}

/**
 * The single read-point for the three onboarding-3.0 platform-trial fields on
 * `CreditOverview` (`trialEndsAt` / `requiresApproval` / `readOnly`). No other
 * component should read those fields directly — see the "one criterion, one
 * place" constraint in the trial-UI spec.
 */
export function usePlatformTrial(): PlatformTrialState {
  const overview = useCreditStore(s => s.overview);

  const awaitingApproval = overview?.requiresApproval === true;
  const readOnly = selectReadOnly(overview);

  const daysLeft = awaitingApproval ? trialDaysLeft(overview?.trialEndsAt, new Date()) : null;

  const availableCredits = overview?.availableCredits;
  const monthlyAllowance = overview?.monthlyAllowance;
  const creditsRemaining =
    awaitingApproval && availableCredits != null ? Math.max(0, Math.floor(availableCredits)) : null;
  const creditsTotal =
    awaitingApproval && monthlyAllowance != null ? Math.floor(monthlyAllowance) : null;

  return {
    awaitingApproval,
    readOnly,
    daysLeft,
    creditsRemaining,
    creditsTotal,
    trialEndsAt: awaitingApproval ? overview?.trialEndsAt ?? null : null,
  };
}
